import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithCredential,
  signOut,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  type User as FirebaseUser,
  TwitterAuthProvider,
  signInWithCustomToken,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  EmailAuthProvider,
  deleteUser,
  linkWithPopup,
  linkWithCredential,
  unlink,
  updateProfile,
} from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { initializeFirebase, auth } from '@/lib/firebase';
import { doc, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { useDisconnect } from 'wagmi';

// Initialize Google Auth for Capacitor
if (typeof window !== 'undefined' && Capacitor.isNativePlatform()) {
  GoogleAuth.initialize({
    clientId: '1067725754607-6pavfn2bp1gk1khsdecnotg6eqqvmete.apps.googleusercontent.com',
    scopes: ['profile', 'email'],
    grantOfflineAccess: true,
  });
}

interface AuthContextType {
  user: FirebaseUser | null;
  loading: boolean;
  signInWithGoogle: (referralCode?: string) => Promise<FirebaseUser>;
  signInWithTwitter: (referralCode?: string) => Promise<FirebaseUser>;
  signInWithWallet: (address: string, signature: string, message: string, referralCode?: string) => Promise<FirebaseUser>;
  signUpWithEmail: (email: string, password: string, referralCode?: string) => Promise<FirebaseUser>;
  signInWithEmail: (email: string, password: string) => Promise<FirebaseUser>;
  resendVerificationEmail: () => Promise<boolean>;
  resetPassword: (email: string) => Promise<void>;
  reauthWithGoogle: () => Promise<void>;
  reauthWithTwitter: () => Promise<void>;
  reauthWithPassword: (password: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  logout: () => Promise<void>;
  linkGoogle: () => Promise<FirebaseUser>;
  linkTwitter: () => Promise<FirebaseUser>;
  linkEmailPassword: (email: string, password: string) => Promise<FirebaseUser>;
  linkWallet: (address: string, signature: string, message: string) => Promise<void>;
  unlinkProvider: (providerId: string) => Promise<void>;
  unlinkWallet: () => Promise<void>;
  updateDisplayName: (newDisplayName: string) => Promise<void>;
  resetDisplayName: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function UserAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const { disconnect } = useDisconnect();

  useEffect(() => {
    let unsub: (() => void) | undefined;
    (async () => {
      try {
        await initializeFirebase();
        unsub = onAuthStateChanged(auth!, (u) => {
          setUser(u);
          setLoading(false);
        });
      } catch (e) {
        console.error('Failed to init Firebase auth', e);
        setLoading(false);
      }
    })();
    return () => {
      if (unsub) unsub();
    };
  }, []);

  // Helper function to save referral data for new users
  const saveReferralData = async (userId: string, manualRefCode?: string) => {
    try {
      const { db } = await initializeFirebase();
      
      // Check for manual referral code first, then URL
      let refCode = manualRefCode;
      if (!refCode) {
        const urlParams = new URLSearchParams(window.location.search);
        refCode = urlParams.get('ref') || undefined;
      }
      
      if (!refCode) return; // No referral code provided
      
      // Look up the referral code to find the referrer
      const codeRef = doc(db, 'referralCodes', refCode.toLowerCase());
      const codeDoc = await getDoc(codeRef);
      
      if (!codeDoc.exists()) {
        console.warn('Invalid referral code:', refCode);
        return; // Invalid referral code
      }
      
      const referrerId = codeDoc.data().userId;
      
      // Save referredBy to the new user's document
      const userRef = doc(db, 'users', userId);
      await setDoc(userRef, {
        referredBy: referrerId,
        referredAt: new Date().toISOString(),
      }, { merge: true });
      
      console.log('Referral data saved:', { userId, referredBy: referrerId, code: refCode });
    } catch (error) {
      console.error('Error saving referral data:', error);
    }
  };

  const signInWithGoogle = async (referralCode?: string) => {
    await initializeFirebase();
    const platform = Capacitor.getPlatform?.() || 'web';
    const isAndroidNative = platform === 'android';
    console.log('[Auth] signInWithGoogle platform:', platform, 'isAndroidNative:', isAndroidNative);

    if (isAndroidNative) {
      console.log('[Auth] Using native GoogleAuth flow');
      // Ensure plugin is initialized before use
      try {
        GoogleAuth.initialize({
          clientId: '1067725754607-6pavfn2bp1gk1khsdecnotg6eqqvmete.apps.googleusercontent.com',
          scopes: ['profile', 'email'],
          grantOfflineAccess: true,
        });
      } catch (e) {
        // initialize may throw if already initialized; ignore
      }

      const googleUser = await GoogleAuth.signIn();
      console.log('[Auth] GoogleAuth.signIn() returned');
      
      // Create Firebase credential from Google token
      const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
      
      // Sign in to Firebase with the credential
      const userCredential = await signInWithCredential(auth!, credential);
      console.log('[Auth] Firebase signInWithCredential success');
      
      // Check if this is a new user and save referral data
      if (userCredential.user.metadata.creationTime === userCredential.user.metadata.lastSignInTime) {
        await saveReferralData(userCredential.user.uid, referralCode);
      }
      
      return userCredential.user;
    } else {
      console.log('[Auth] Using web popup flow');
      // Use popup for web
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const res = await signInWithPopup(auth!, provider);
      
      // Check if this is a new user and save referral data
      if (res.user.metadata.creationTime === res.user.metadata.lastSignInTime) {
        await saveReferralData(res.user.uid, referralCode);
      }
      
      return res.user;
    }
  };

  const resetPassword = async (email: string) => {
    await initializeFirebase();
    await sendPasswordResetEmail(auth!, email);
  };

  const signInWithWallet = async (address: string, signature: string, message: string, referralCode?: string) => {
    await initializeFirebase();
    const baseUrl = window.location.origin;
    const res = await fetch(`${baseUrl}/.netlify/functions/create-wallet-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ address, signature, message }),
    });
    if (!res.ok) {
      let text = 'Failed to create wallet token';
      try { const j = await res.json(); text = j.error || text; } catch {}
      throw new Error(text);
    }
    const { customToken } = await res.json();
    const userCred = await signInWithCustomToken(auth!, customToken);
    
    // Check if this is a new user and save referral data
    if (userCred.user.metadata.creationTime === userCred.user.metadata.lastSignInTime) {
      await saveReferralData(userCred.user.uid, referralCode);
    }
    
    return userCred.user;
  };

  const signUpWithEmail = async (email: string, password: string, referralCode?: string) => {
    await initializeFirebase();
    const res = await createUserWithEmailAndPassword(auth!, email, password);
    
    // Save referral data for new email signups
    await saveReferralData(res.user.uid, referralCode);
    
    try {
      await sendEmailVerification(res.user, {
        url: window.location.origin,
        handleCodeInApp: false,
      });
    } catch (e) {
      console.warn('Email verification send failed', e);
    }
    return res.user;
  };

  const signInWithEmail = async (email: string, password: string) => {
    await initializeFirebase();
    const res = await signInWithEmailAndPassword(auth!, email, password);
    return res.user;
  };

  const resendVerificationEmail = async () => {
    await initializeFirebase();
    if (auth!.currentUser && !auth!.currentUser.emailVerified) {
      await sendEmailVerification(auth!.currentUser, {
        url: window.location.origin,
        handleCodeInApp: false,
      });
      return true;
    }
    return false;
  };

  const logout = async () => {
    await initializeFirebase();
    
    // Check if user is signed in with wallet (uid starts with 'evm:')
    const isWalletUser = auth!.currentUser?.uid?.startsWith('evm:');
    
    // Disconnect wallet if signed in with wallet
    if (isWalletUser) {
      try {
        disconnect();
        console.log('Wallet disconnected on logout');
      } catch (error) {
        console.error('Failed to disconnect wallet on logout:', error);
      }
    }
    
    await signOut(auth!);
  };

  const signInWithTwitter = async (referralCode?: string) => {
    await initializeFirebase();
    const provider = new TwitterAuthProvider();
    const res = await signInWithPopup(auth!, provider);
    
    // Check if this is a new user and save referral data
    if (res.user.metadata.creationTime === res.user.metadata.lastSignInTime) {
      await saveReferralData(res.user.uid, referralCode);
    }
    
    return res.user;
  };

  const reauthWithGoogle = async () => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not signed in');
    const provider = new GoogleAuthProvider();
    await reauthenticateWithPopup(auth!.currentUser, provider);
  };

  const reauthWithTwitter = async () => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not signed in');
    const provider = new TwitterAuthProvider();
    await reauthenticateWithPopup(auth!.currentUser, provider);
  };

  const reauthWithPassword = async (password: string) => {
    await initializeFirebase();
    const u = auth!.currentUser;
    if (!u || !u.email) throw new Error('Email required for password re-auth');
    const cred = EmailAuthProvider.credential(u.email, password);
    await reauthenticateWithCredential(u, cred);
  };

  const deleteAccount = async () => {
    await initializeFirebase();
    const u = auth!.currentUser;
    if (!u) throw new Error('Not signed in');
    await deleteUser(u);
  };

  // Link Google provider to existing account
  const linkGoogle = async () => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not authenticated');
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });
    const res = await linkWithPopup(auth!.currentUser, provider);
    return res.user;
  };

  // Link Twitter provider to existing account
  const linkTwitter = async () => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not authenticated');
    const provider = new TwitterAuthProvider();
    const res = await linkWithPopup(auth!.currentUser, provider);
    return res.user;
  };

  // Link Email/Password to existing account
  const linkEmailPassword = async (email: string, password: string) => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not authenticated');
    const credential = EmailAuthProvider.credential(email, password);
    const res = await linkWithCredential(auth!.currentUser, credential);
    return res.user;
  };

  // Link Wallet to existing account
  const linkWallet = async (address: string, signature: string, message: string) => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not authenticated');
    
    const response = await fetch(`${window.location.origin}/.netlify/functions/link-wallet`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: auth!.currentUser.uid,
        address,
        signature,
        message,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to link wallet');
    }
  };

  // Unlink a provider (Google, Twitter, Email/Password)
  const unlinkProvider = async (providerId: string) => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not authenticated');
    
    // Check if user has at least 2 providers before unlinking
    if (auth!.currentUser.providerData.length <= 1) {
      throw new Error('Cannot unlink the only sign-in method. Please link another method first.');
    }
    
    await unlink(auth!.currentUser, providerId);
  };

  // Unlink wallet
  const unlinkWallet = async () => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not authenticated');
    
    const { db } = await initializeFirebase();
    const userRef = doc(db, 'users', auth!.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists() && userDoc.data().linkedWallet) {
      const walletAddress = userDoc.data().linkedWallet;
      
      // Remove from linkedWallets collection
      const walletRef = doc(db, 'linkedWallets', walletAddress);
      await deleteDoc(walletRef);
      
      // Remove from user document
      await setDoc(userRef, {
        linkedWallet: null,
        walletLinkedAt: null,
      }, { merge: true });
    }
  };

  // Update display name
  const updateDisplayName = async (newDisplayName: string) => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not authenticated');
    
    const { db } = await initializeFirebase();
    const userRef = doc(db, 'users', auth!.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    // Store original display name if not already stored
    if (userDoc.exists() && !userDoc.data().originalDisplayName) {
      await setDoc(userRef, {
        originalDisplayName: auth!.currentUser.displayName || '',
      }, { merge: true });
    }
    
    // Update Firebase Auth profile
    await updateProfile(auth!.currentUser, {
      displayName: newDisplayName,
    });
    
    // Store custom display name in Firestore
    await setDoc(userRef, {
      customDisplayName: newDisplayName,
    }, { merge: true });
    
    // Trigger user state update
    setUser({ ...auth!.currentUser });
  };

  // Reset display name to original
  const resetDisplayName = async () => {
    await initializeFirebase();
    if (!auth!.currentUser) throw new Error('Not authenticated');
    
    const { db } = await initializeFirebase();
    const userRef = doc(db, 'users', auth!.currentUser.uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists() && userDoc.data().originalDisplayName) {
      const originalName = userDoc.data().originalDisplayName;
      
      // Update Firebase Auth profile
      await updateProfile(auth!.currentUser, {
        displayName: originalName,
      });
      
      // Remove custom display name from Firestore
      await setDoc(userRef, {
        customDisplayName: null,
      }, { merge: true });
      
      // Trigger user state update
      setUser({ ...auth!.currentUser });
    }
  };

  const value = useMemo<AuthContextType>(() => ({
    user,
    loading,
    signInWithGoogle,
    signInWithTwitter,
    signInWithWallet,
    signUpWithEmail,
    signInWithEmail,
    resendVerificationEmail,
    resetPassword,
    reauthWithGoogle,
    reauthWithTwitter,
    reauthWithPassword,
    deleteAccount,
    logout,
    linkGoogle,
    linkTwitter,
    linkEmailPassword,
    linkWallet,
    unlinkProvider,
    unlinkWallet,
    updateDisplayName,
    resetDisplayName,
  }), [user, loading]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useUserAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useUserAuth must be used within UserAuthProvider');
  return ctx;
}
